import React, { useState, useEffect } from 'react';
import {
  Zap, Settings, Play, Pause, RotateCcw, Plus, Filter, Calendar, 
  RefreshCw, TrendingUp, TrendingDown, Target, Award, Users, 
  Clock, CheckCircle, AlertTriangle, Eye, Download, BarChart3,
  FileText, BookOpen, Timer, Save, Send, FilePlus, Tag, Flag,
  Layers, Grid, List, MoreVertical, ThumbsUp, ThumbsDown, Folder,
  User, Mail, Phone, MapPin, Globe, Key, Smartphone, EyeOff,
  Bell, Lock, Unlock, Shield, Info, HelpCircle, X, Check, Minus,
  Activity, PieChart, LineChart, BarChart, Gauge, Thermometer,
  ChevronUp, ChevronDown, MoreHorizontal, ExternalLink,
  MessageSquare, Archive, Copy, Share2, Trash2, Edit, Search,
  ChevronRight, ChevronLeft, Maximize2, Minimize2, Move, RotateCw,
  Wifi, WifiOff, Signal, Battery, BatteryLow, Volume2, VolumeX,
  Mic, MicOff, Camera, CameraOff, Video, VideoOff, Headphones,
  HeadphonesOff, Monitor, MonitorOff, Laptop, Tablet, Watch, Tv,
  Gamepad2, Mouse, Keyboard, Printer, Scanner, HardDrive, Cpu,
  MemoryStick, Database, Server, Cloud, CloudOff, Upload,
  Download as DownloadIcon, Link, LinkOff, Share, ShareOff,
  Lock as LockIcon, Unlock as UnlockIcon, Key as KeyIcon,
  Fingerprint, Shield as ShieldIcon, ShieldCheck, ShieldAlert,
  AlertCircle, AlertTriangle as AlertTriangleIcon, Info as InfoIcon,
  HelpCircle as HelpCircleIcon, CheckCircle as CheckCircleIcon,
  XCircle, MinusCircle, PlusCircle, PlayCircle, PauseCircle,
  StopCircle, SkipBack, SkipForward, Rewind, FastForward,
  Repeat, Shuffle, Volume1, Volume2 as Volume2Icon,
  VolumeX as VolumeXIcon, Mic as MicIcon, MicOff as MicOffIcon,
  Camera as CameraIcon, CameraOff as CameraOffIcon,
  Video as VideoIcon, VideoOff as VideoOffIcon,
  Headphones as HeadphonesIcon, HeadphonesOff as HeadphonesOffIcon,
  Monitor as MonitorIcon, MonitorOff as MonitorOffIcon,
  Laptop as LaptopIcon, Tablet as TabletIcon, Watch as WatchIcon,
  Tv as TvIcon, Gamepad2 as Gamepad2Icon, Mouse as MouseIcon,
  Keyboard as KeyboardIcon, Printer as PrinterIcon,
  Scanner as ScannerIcon, HardDrive as HardDriveIcon,
  Cpu as CpuIcon, MemoryStick as MemoryStickIcon,
  Database as DatabaseIcon, Server as ServerIcon,
  Cloud as CloudIcon, CloudOff as CloudOffIcon,
  Upload as UploadIcon, Link as LinkIcon, LinkOff as LinkOffIcon,
  Share as ShareIcon, ShareOff as ShareOffIcon,
  Fingerprint as FingerprintIcon, ShieldCheck as ShieldCheckIcon,
  ShieldAlert as ShieldAlertIcon, AlertCircle as AlertCircleIcon,
  XCircle as XCircleIcon, MinusCircle as MinusCircleIcon,
  PlusCircle as PlusCircleIcon, PlayCircle as PlayCircleIcon,
  PauseCircle as PauseCircleIcon, StopCircle as StopCircleIcon,
  SkipBack as SkipBackIcon, SkipForward as SkipForwardIcon,
  Rewind as RewindIcon, FastForward as FastForwardIcon,
  Repeat as RepeatIcon, Shuffle as ShuffleIcon,
  Volume1 as Volume1Icon, Battery as BatteryIcon,
  BatteryLow as BatteryLowIcon, Signal as SignalIcon,
  Wifi as WifiIcon, WifiOff as WifiOffIcon,
  Maximize2 as Maximize2Icon, Minimize2 as Minimize2Icon,
  Move as MoveIcon, RotateCw as RotateCwIcon
} from 'lucide-react';
import MockDataService from '../../../services/MockDataService';

const AgentWorkflowOptimization = () => {
  const [workflowData, setWorkflowData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAutomation, setSelectedAutomation] = useState(null);

  useEffect(() => {
    const fetchWorkflowData = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getAgentWorkflowOptimization('agent-001');
        if (response.success) {
          setWorkflowData(response.data);
        } else {
          setError('Failed to load workflow data');
        }
      } catch (err) {
        setError('An error occurred while fetching workflow data.');
        console.error('Error fetching workflow data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflowData();
  }, []);

  if (loading) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading workflow optimization...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500 dark:text-red-400">Error: {error}</div>;
  }

  if (!workflowData) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">No workflow data available.</div>;
  }

  const { agentWorkflowOptimization, workflowStats } = workflowData;
  const { workflowAutomation, taskTemplates, quickActions, workflowAnalytics, processOptimization, automationRules, workflowMonitoring, efficiencyMetrics } = agentWorkflowOptimization;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 dark:text-green-400';
      case 'inactive': return 'text-red-600 dark:text-red-400';
      case 'paused': return 'text-yellow-600 dark:text-yellow-400';
      case 'normal': return 'text-green-600 dark:text-green-400';
      case 'warning': return 'text-yellow-600 dark:text-yellow-400';
      case 'critical': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'inactive': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'paused': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'normal': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />;
      case 'stable': return <Activity className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
      default: return <Activity className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Zap className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Workflow Optimization
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Automate tasks and optimize your workflow
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                <FileText className="h-4 w-4 inline-block mr-2" />
                Templates
              </button>
              <button className="px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                <Plus className="h-4 w-4 inline-block mr-2" />
                New Rule
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Workflow Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Tasks Saved</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{workflowStats.tasksAutomatedPerDay}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Per day</p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <CheckCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Time Saved</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{workflowStats.timeSavedPerDay}h</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Per day</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Clock className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Efficiency Score</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{efficiencyMetrics.overallEfficiency}%</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Target: 90%</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Target className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Automation Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{workflowAnalytics.efficiencyMetrics.find(m => m.name === 'Automation Rate')?.value}%</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Target: 70%</p>
              </div>
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Zap className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Savings Achieved</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">${workflowStats.costSavingsPerMonth}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Per month</p>
              </div>
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                <Award className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', name: 'Overview', icon: BarChart3 },
                { id: 'automations', name: 'Automations', icon: Zap },
                { id: 'templates', name: 'Templates', icon: FileText },
                { id: 'quickactions', name: 'Quick Actions', icon: Play },
                { id: 'analytics', name: 'Analytics', icon: TrendingUp },
                { id: 'monitoring', name: 'Monitoring', icon: Activity }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <tab.icon className="h-4 w-4 inline-block mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Active Automations */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Active Automations</h3>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Automation</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Runs</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Success Rate</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Run</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {workflowAutomation.automations.map((automation) => (
                            <tr key={automation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-lg font-bold">
                                    {automation.name.charAt(0)}
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{automation.name}</div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">{automation.description}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  {automation.type}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(automation.status)}`}>
                                  {automation.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {automation.runCount}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {automation.successRate}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {automation.lastRun ? new Date(automation.lastRun).toLocaleString() : 'Never'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <button className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300">
                                    <Play className="h-4 w-4" />
                                  </button>
                                  <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300">
                                    <Settings className="h-4 w-4" />
                                  </button>
                                  <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Efficiency Metrics */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Efficiency Metrics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {workflowAnalytics.efficiencyMetrics.map((metric) => (
                      <div key={metric.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{metric.name}</h4>
                          <div className="flex items-center space-x-2">
                            {getTrendIcon(metric.trend)}
                            <span className={`text-sm font-medium ${
                              metric.status === 'excellent' ? 'text-green-600 dark:text-green-400' :
                              metric.status === 'good' ? 'text-blue-600 dark:text-blue-400' :
                              metric.status === 'average' ? 'text-yellow-600 dark:text-yellow-400' :
                              'text-red-600 dark:text-red-400'
                            }`}>
                              {metric.status}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Current:</span>
                            <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                              {metric.value} {metric.unit}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Target:</span>
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {metric.target} {metric.unit}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Change:</span>
                            <span className={`text-sm font-medium ${metric.changePercentage >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {metric.changePercentage >= 0 ? '+' : ''}{metric.changePercentage}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Automations Tab */}
            {activeTab === 'automations' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Workflow Automations</h3>
                  <button className="px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                    <Plus className="h-4 w-4 inline-block mr-2" />
                    Create Automation
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {workflowAutomation.automations.map((automation) => (
                    <div key={automation.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{automation.name}</h4>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(automation.status)}`}>
                            {automation.status}
                          </span>
                          <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300">
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{automation.description}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Type:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{automation.type}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Runs:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{automation.runCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Success Rate:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{automation.successRate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Avg Time:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{automation.averageExecutionTime}ms</span>
                        </div>
                      </div>
                      <div className="mt-4 flex space-x-2">
                        <button className="flex-1 px-3 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                          <Play className="h-4 w-4 inline-block mr-2" />
                          Run
                        </button>
                        <button className="flex-1 px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <Settings className="h-4 w-4 inline-block mr-2" />
                          Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Task Templates</h3>
                  <button className="px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                    <Plus className="h-4 w-4 inline-block mr-2" />
                    Create Template
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {taskTemplates.map((template) => (
                    <div key={template.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{template.name}</h4>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            template.difficulty === 'easy' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            template.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {template.difficulty}
                          </span>
                          <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300">
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{template.description}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Category:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{template.category}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Steps:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{template.steps.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Est. Time:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{template.estimatedTime} min</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Usage:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{template.usageCount} times</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Success Rate:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{template.successRate}%</span>
                        </div>
                      </div>
                      <div className="mt-4 flex space-x-2">
                        <button className="flex-1 px-3 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                          <Play className="h-4 w-4 inline-block mr-2" />
                          Use Template
                        </button>
                        <button className="flex-1 px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <Edit className="h-4 w-4 inline-block mr-2" />
                          Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions Tab */}
            {activeTab === 'quickactions' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Quick Actions</h3>
                  <button className="px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                    <Plus className="h-4 w-4 inline-block mr-2" />
                    Add Action
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {quickActions.map((action) => (
                    <div key={action.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center">
                      <div className="text-4xl mb-4">{action.icon}</div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{action.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{action.description}</p>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Shortcut:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{action.shortcut}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Usage:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{action.usageCount} times</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Category:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">{action.category}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          action.isEnabled 
                            ? 'bg-green-600 text-white hover:bg-green-700' 
                            : 'bg-gray-600 text-white hover:bg-gray-700'
                        }`}>
                          {action.isEnabled ? 'Enabled' : 'Disabled'}
                        </button>
                        <button className="px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <Settings className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Workflow Analytics</h3>
                
                {/* Time Analysis */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">Time Analysis</h4>
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Task</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Avg Time</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Optimized</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Time Saved</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Improvement</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total Saved</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {workflowAnalytics.timeAnalysis.map((analysis) => (
                            <tr key={analysis.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                                {analysis.task}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {analysis.averageTime} min
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {analysis.optimizedTime} min
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {analysis.timeSaved} min
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {analysis.improvementPercentage}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                {analysis.totalTimeSaved} min/day
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Optimization Opportunities */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">Optimization Opportunities</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {workflowAnalytics.optimizationOpportunities.map((opportunity) => (
                      <div key={opportunity.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{opportunity.title}</h5>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            opportunity.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                            opportunity.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                            'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          }`}>
                            {opportunity.priority} priority
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{opportunity.description}</p>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Potential Savings:</span>
                            <span className="text-sm text-gray-900 dark:text-gray-100">{opportunity.potentialSavings} hours/day</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Effort:</span>
                            <span className="text-sm text-gray-900 dark:text-gray-100">{opportunity.implementationEffort}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">ROI:</span>
                            <span className="text-sm text-gray-900 dark:text-gray-100">{opportunity.estimatedROI}%</span>
                          </div>
                        </div>
                        <div className="mt-4">
                          <button className="w-full px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                            Implement Opportunity
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Monitoring Tab */}
            {activeTab === 'monitoring' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Workflow Monitoring</h3>
                
                {/* Active Monitors */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">Active Monitors</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {workflowMonitoring.activeMonitors.map((monitor) => (
                      <div key={monitor.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{monitor.name}</h5>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(monitor.status)}`}>
                            {monitor.status}
                          </span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Type:</span>
                            <span className="text-sm text-gray-900 dark:text-gray-100">{monitor.type}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Target:</span>
                            <span className="text-sm text-gray-900 dark:text-gray-100">{monitor.target}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Threshold:</span>
                            <span className="text-sm text-gray-900 dark:text-gray-100">{monitor.threshold}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Current:</span>
                            <span className="text-sm text-gray-900 dark:text-gray-100">{monitor.currentValue}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Last Check:</span>
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {new Date(monitor.lastCheck).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Alerts */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Alerts</h4>
                  <div className="space-y-2">
                    {workflowMonitoring.alerts.map((alert) => (
                      <div key={alert.id} className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex-shrink-0">
                          <div className={`w-2 h-2 rounded-full ${
                            alert.severity === 'high' ? 'bg-red-500' :
                            alert.severity === 'warning' ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}></div>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm text-gray-900 dark:text-gray-100">{alert.message}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(alert.timestamp).toLocaleString()}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            alert.severity === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                            alert.severity === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                            'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          }`}>
                            {alert.severity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentWorkflowOptimization;
